// src/lib/pharmacyFirst/pathways/aht/aht-wt-mysimba-transfer.pathway.ts
//
// AHT Weight Management — Mysimba® (naltrexone/bupropion) Transfer Supply Pathway
// Source: AHT_Mysimba_Clinical_Pathway.docx v1.0
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// For patients transferring from another provider already established on Mysimba.

import type { Pathway } from "../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q0_RED_FLAGS      = "mys-trans-q0-red-flags";
const Q1_F2F            = "mys-trans-q1-f2f";
const CONFIRM_CARD      = "mys-trans-confirmation-card";
const PGD_FRESH_CHECKS  = "mys-trans-pgd-fresh-checks";
const Q4_INT_MAOI       = "mys-trans-int1-maoi";
const Q5_INT_OPIOID     = "mys-trans-int2-opioid";
const Q6_INT_CYP2D6     = "mys-trans-int3-cyp2d6";
const Q7_INT_SEIZURE    = "mys-trans-int4-seizure-threshold";
const Q8_INT_CYP2B6     = "mys-trans-int5-cyp2b6-inducer";
const Q9_CAUTIONS       = "mys-trans-cautions";
const Q10_TRANSFER_FLAG = "mys-trans-transfer-flag";
const Q11_DOSE          = "mys-trans-dose-select";
const Q12_ATTEST        = "mys-trans-attest";

const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtMysimbaTransferPathway: Pathway = {
  id: "aht-wt-mysimba-transfer",
  name: "Mysimba® (naltrexone/bupropion) — Transfer Supply",
  population: "Adults transferring from another provider already established on Mysimba",
  pgdRefs: ["pgd-aht-wt-mysimba"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q0_RED_FLAGS,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 0 — Red flags gateway (includes BP reminder)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q0_RED_FLAGS,
      type: "gateway_progression",
      triggersPayment: false,
      heading: "Measure BP and pulse — check for red flags before proceeding",
      body:
        "Measure BP and pulse NOW before proceeding.\n" +
        "Do not proceed if any of the following are present:\n" +
        "• Uncontrolled hypertension — discontinue\n" +
        "• New or worsening seizure disorder\n" +
        "• Suicidal ideation or significant mood change — refer urgently\n" +
        "• New opioid use (naltrexone blocks analgesia — warn patient)\n" +
        "• Signs of hepatotoxicity (jaundice, right upper quadrant pain, dark urine) — stop\n" +
        "• Pregnancy confirmed or suspected — discontinue\n" +
        "If any red flag is present, do not proceed — refer to GP or A&E as appropriate.",
      next: Q1_F2F,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedFaceToFace"] === false,
          then: { outcomeId: OUT_NO_F2F },
        },
      ],
      default: "mys-trans-q-pregnancy",
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3a — Pregnancy / breastfeeding check
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: "mys-trans-q-pregnancy",
      type: "yes_no",
      text: "Is the patient pregnant, breastfeeding, or actively attempting to conceive?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3b — PGD inclusion / exclusion checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: PGD_FRESH_CHECKS,
      type: "pgd_exclusion_check",
      variant: "sequential",
      pgdIdOverride: "pgd-aht-wt-mysimba",
      anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
      allCleared: Q4_INT_MAOI,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 4 — Interaction checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q4_INT_MAOI,
      type: "condition_route",
      conditions: [],
      default: Q5_INT_OPIOID,
    },

    {
      id: Q5_INT_OPIOID,
      type: "condition_route",
      conditions: [],
      default: Q6_INT_CYP2D6,
    },

    {
      id: Q6_INT_CYP2D6,
      type: "yes_no",
      text: "Is the patient taking CYP2D6 substrates (SSRIs, tricyclic antidepressants, antipsychotics, metoprolol, propafenone, or flecainide)?",
      hint: "Bupropion inhibits CYP2D6 — initiate at lower dose; therapeutic drug monitoring for narrow-index drugs.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document any anticipated dose adjustments and monitoring plan for CYP2D6 substrates",
        acknowledgementText: "Patient and prescriber have been informed of potential drug interaction and monitoring requirements",
      },
      yes: Q7_INT_SEIZURE,
      no: Q7_INT_SEIZURE,
    },

    {
      id: Q7_INT_SEIZURE,
      type: "yes_no",
      text: "Is the patient taking drugs that lower the seizure threshold (antipsychotics, antidepressants, tramadol, theophylline, quinolones, steroids, or antimalarials)?",
      hint: "Increased seizure risk.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the seizure risk assessment and monitoring plan",
        acknowledgementText: "Patient has been counselled on increased seizure risk and understands the monitoring plan",
      },
      yes: Q8_INT_CYP2B6,
      no: Q8_INT_CYP2B6,
    },

    {
      id: Q8_INT_CYP2B6,
      type: "yes_no",
      text: "Is the patient taking CYP2B6 enzyme inducers (carbamazepine, phenytoin, ritonavir, or efavirenz)?",
      hint: "May significantly reduce bupropion exposure.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document any anticipated bupropion dose adjustments and monitoring plan",
        acknowledgementText: "Patient has been informed of potential reduced bupropion efficacy and monitoring requirements",
      },
      yes: Q9_CAUTIONS,
      no: Q9_CAUTIONS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Cautions review
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q9_CAUTIONS,
      type: "flag",
      variant: "warn",
      message:
        "Review cautions before supply:\n" +
        "• BP and pulse: measure before initiation, after each dose escalation, and at every review\n" +
        "• Seizure risk (~0.1%) dose-related — predisposing factors include head trauma, excessive alcohol, cocaine/stimulants\n" +
        "• Suicidality: monitor closely for clinical worsening, suicidal ideation, unusual behavioural changes\n" +
        "• Opioid blockade: naltrexone blocks opioid receptors — patients will not receive full pain relief; warn explicitly\n" +
        "• Attempting to overcome opioid blockade with large opioid doses is dangerous and potentially fatal\n" +
        "• Serotonin syndrome risk with SSRIs/SNRIs\n" +
        "• Hepatotoxicity: drug-induced liver injury reported — discontinue if suspected\n" +
        "• Alcohol: minimise or avoid throughout treatment",
      next: Q10_TRANSFER_FLAG,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Transfer notice
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q10_TRANSFER_FLAG,
      type: "flag",
      variant: "info",
      message:
        "Transfer from another provider — patient is already established on this medication.\n" +
        "Confirm the patient's current dose before supplying.\n" +
        "• Request evidence of previous supply (prescription, dispensing label, or transfer letter)\n" +
        "• Verify the dose matches the patient's stated current regimen\n" +
        "• Supply at the established dose — do not restart dose escalation",
      next: Q11_DOSE,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 7 — Current dose selection
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q11_DOSE,
      type: "single_select",
      text: "What dose should be supplied?",
      options: [
        { id: "mys-week1",       label: "Week 1 dose — 1 tablet each morning" },
        { id: "mys-week2",       label: "Week 2 dose — 1 morning + 1 evening" },
        { id: "mys-week3",       label: "Week 3 dose — 2 morning + 1 evening" },
        { id: "mys-maintenance", label: "Maintenance dose — 2 morning + 2 evening (MAX)" },
      ],
      routing: {
        "mys-week1":       Q12_ATTEST,
        "mys-week2":       Q12_ATTEST,
        "mys-week3":       Q12_ATTEST,
        "mys-maintenance": Q12_ATTEST,
      },
      storesTo: "selectedDoseStep",
      required: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 8 — Supply attestation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q12_ATTEST,
      type: "attestation",
      text: "Confirm transfer supply of Mysimba® (naltrexone/bupropion)",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied Mysimba® naltrexone/bupropion at the patient's confirmed established dose\n" +
        "• Verified the dose against evidence of previous supply\n" +
        "• Confirmed tablets must be swallowed whole — not cut, chewed, or crushed\n" +
        "• Confirmed patient carries patient card AT ALL TIMES — opioid blockade warning\n" +
        "• Measured and recorded BP and pulse before supply\n" +
        "• Advised the patient to inform their GP of the provider transfer\n" +
        "• Recorded batch number and expiry date",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    [OUT_NO_F2F]: {
      id: OUT_NO_F2F,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — face-to-face consultation required",
      reason: "Supply under this PGD requires the patient to attend in person at AHT registered premises.",
      safetyNetting: ["Ask the patient to book an in-person appointment to proceed with supply."],
    },
    [OUT_CANNOT_SUPPLY]: {
      id: OUT_CANNOT_SUPPLY,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — Mysimba® (naltrexone/bupropion)",
      reason: "An inclusion criterion was not met, an exclusion criterion was triggered, or a contraindicated drug interaction was identified.",
      safetyNetting: [
        "Discuss the reason supply cannot proceed with the patient.",
        "Consider referral to prescriber or GP if clinically appropriate.",
      ],
    },
    [OUT_SUPPLY]: {
      id: OUT_SUPPLY,
      type: "outcome",
      outcomeType: "supply",
      title: "Transfer supply complete — Mysimba® (naltrexone/bupropion)",
      reason: "All inclusion criteria met, no exclusion criteria triggered, and transfer supply attestation confirmed.",
      drugRef: "mysimba",
      safetyNetting: [
        "Return in 4 weeks for monthly monitoring review.",
        "Carry patient card at all times — opioid blockade warning.",
        "Report thoughts of self-harm, suicide, or unusual mood changes immediately.",
        "Seek urgent help if seizure occurs — STOP Mysimba®.",
        "Avoid alcohol throughout treatment.",
      ],
    },
  },
};
